class Accordion {
    #reducedMotionMediaQuery;
    constructor(el) {
        this.#reducedMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.el = el;
        this.button = el.querySelector("button[aria-expanded]") || el.querySelector("summary") || el.querySelector(".faq__accordion-heading, .product-single__accordion-heading");
        this.summary = this.button;
        this.content = el.querySelector("details-content, .faq__accordion-panel, .product-single__accordion-panel, [role='region'], .product-single__accordion__item-wrap");
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        const heading = el.querySelector(".faq__accordion-heading, .product-single__accordion-heading") || this.button;
        if (heading) {
            heading.addEventListener("click", (e => this.onClick(e)));
            const summaryIcon = heading.querySelector("summary-icon");
            if (summaryIcon) {
                summaryIcon.setAttribute("aria-hidden", "true");
            }
        } else if (this.button) {
            this.button.addEventListener("click", (e => this.onClick(e)));
            const summaryIcon = this.button.querySelector("summary-icon");
            if (summaryIcon) {
                summaryIcon.setAttribute("aria-hidden", "true");
            }
        }
        this.elementToScrollTo = document.getElementById(this.el.dataset.scrollToElementIdOnClose);
    }
    get isOpen() {
        if (this.button && this.button.hasAttribute("aria-expanded")) {
            return this.button.getAttribute("aria-expanded") === "true";
        }
        return this.el.open;
    }
    set isOpen(open) {
        if (this.el.tagName.toLowerCase() === "details") {
            this.el.open = open;
        }
        if (this.button && this.button.hasAttribute("aria-expanded")) {
            this.button.setAttribute("aria-expanded", open ? "true" : "false");
        }
        if (this.content) {
            if (open) {
                this.content.removeAttribute("hidden");
                this.content.style.display = "block";
            } else {
                this.content.setAttribute("hidden", "");
                this.content.style.display = "none";
            }
        }
    }
    onClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.el.hasAttribute("non-dismissible")) {
            return;
        }
        const wasOpen = this.isOpen;
        if (this.button && this.button.hasAttribute("aria-expanded")) {
            this.button.setAttribute("aria-expanded", wasOpen ? "false" : "true");
        }
        if (wasOpen) {
            this.shrink();
        } else {
            this.open();
        }
        if (this.button && typeof this.button.focus === "function") {
            this.button.blur();
            setTimeout(() => {
                this.button.focus({ preventScroll: true });
            }, 100);
        }
    }
    shrink() {
        if (!this.content) {
            this.onAnimationFinish(false);
            return;
        }
        this.el.dispatchEvent(new CustomEvent("on:accordion:closing"));
        this.isClosing = true;
        this.el.classList.add("is-closing");

        if (this.button && this.button.hasAttribute("aria-expanded")) {
            this.button.setAttribute("aria-expanded", "false");
        }

        if (this.el.tagName.toLowerCase() === "details" && this.el.open) {
            this.content.style.display = "block";
            this.el.open = false;
        }

        this.content.style.overflow = "hidden";
        const computedStyle = window.getComputedStyle(this.content);
        const currentPaddingTop = computedStyle.paddingTop;
        const currentPaddingBottom = computedStyle.paddingBottom;
        const currentHeight = `${this.content.offsetHeight}px`;

        if (this.elementToScrollTo) {
            this.elementToScrollTo.scrollIntoView({
                behavior: "smooth"
            });
        }
        if (this.animation) {
            this.animation.cancel();
            this.el.dispatchEvent(new CustomEvent("on:accordion:cancelled"));
        }
        this.animation = this.content.animate({
            height: [ currentHeight, "0px" ],
            paddingTop: [ currentPaddingTop, "0px" ],
            paddingBottom: [ currentPaddingBottom, "0px" ],
            opacity: [ 1, 0 ]
        }, {
            duration: this.#reducedMotionMediaQuery.matches ? 0 : 300,
            easing: "cubic-bezier(0.25, 1, 0.5, 1)"
        });
        this.animation.onfinish = () => {
            this.onAnimationFinish(false);
            this.el.dispatchEvent(new CustomEvent("on:accordion:closed"));
        };
        this.animation.oncancel = () => {
            this.isClosing = false;
            this.el.classList.remove("is-closing");
        };
    }
    open() {
        if (!this.content) {
            this.onAnimationFinish(true);
            return;
        }
        this.el.dispatchEvent(new CustomEvent("on:accordion:opening"));
        this.isExpanding = true;
        this.el.classList.add("is-opening");

        if (this.button && this.button.hasAttribute("aria-expanded")) {
            this.button.setAttribute("aria-expanded", "true");
        }

        if (this.el.tagName.toLowerCase() === "details" && !this.el.open) {
            this.el.open = true;
        }

        this.content.style.display = "block";
        this.content.style.overflow = "hidden";
        this.content.removeAttribute("hidden");

        const computedStyle = window.getComputedStyle(this.content);
        const targetPaddingTop = computedStyle.paddingTop;
        const targetPaddingBottom = computedStyle.paddingBottom;

        void this.content.offsetHeight;
        const targetHeight = this.content.scrollHeight || this.content.offsetHeight;

        if (this.animation) {
            this.animation.cancel();
            this.el.dispatchEvent(new CustomEvent("on:accordion:cancelled"));
        }

        this.animation = this.content.animate({
            height: [ "0px", `${targetHeight}px` ],
            paddingTop: [ "0px", targetPaddingTop ],
            paddingBottom: [ "0px", targetPaddingBottom ],
            opacity: [ 0, 1 ]
        }, {
            duration: this.#reducedMotionMediaQuery.matches ? 0 : 350,
            easing: "cubic-bezier(0.25, 1, 0.5, 1)"
        });
        this.animation.onfinish = () => {
            this.onAnimationFinish(true);
            this.el.dispatchEvent(new CustomEvent("on:accordion:opened"));
        };
        this.animation.oncancel = () => {
            this.isExpanding = false;
            this.el.classList.remove("is-opening");
        };
    }
    expand() {
        this.open();
    }
    onAnimationFinish(open) {
        this.isOpen = open;
        this.animation = null;
        this.isClosing = false;
        this.el.classList.remove("is-closing");
        this.isExpanding = false;
        this.el.classList.remove("is-opening");
        if (this.content) {
            this.content.style.height = "";
            this.content.style.paddingTop = "";
            this.content.style.paddingBottom = "";
            this.content.style.overflow = "";
            this.content.style.opacity = "";
            if (!open) {
                this.content.setAttribute("hidden", "");
                this.content.style.display = "none";
            } else {
                this.content.removeAttribute("hidden");
                this.content.style.display = "block";
            }
        }
    }
}

export { Accordion };

class AccordionGroup extends HTMLElement {
    #alwaysOpen;
    #boundHandleNonDismissible;
    #boundHandleSingleOpen;
    #singleOpen;
    constructor() {
        super();
        this.#boundHandleSingleOpen = this.#handleSingleOpen.bind(this);
        this.#boundHandleNonDismissible = this.#handleNonDismissible.bind(this);
    }
    connectedCallback() {
        this.#singleOpen = this.hasAttribute("single-open");
        this.#alwaysOpen = this.hasAttribute("always-open");
        this.triggers = this.querySelectorAll("summary, button[aria-expanded]");
        this.details = this.querySelectorAll("details, .faq__accordion-item, .product-single__accordion-item");
        this.accordions = {};
        if (this.details.length === 0) return;
        this.details.forEach((el => {
            if (el.id) {
                this.accordions[el.id] = new Accordion(el);
            }
        }));
        if (this.#singleOpen) {
            const openAccordions = Array.from(this.querySelectorAll("details[open], .faq__accordion-item:has(button[aria-expanded='true']), .product-single__accordion-item:has(button[aria-expanded='true'])"));
            if (openAccordions.length > 0) {
                openAccordions.forEach(((el, index) => {
                    if (index > 0 && el.id) this.close(el.id);
                }));
            } else if (this.details[0] && this.details[0].id) {
                this.open(this.details[0].id);
            }
            this.details.forEach((details => {
                details.addEventListener("on:accordion:opening", this.#boundHandleSingleOpen);
            }));
        }
        if (this.#alwaysOpen) {
            let currentlyOpen = this.querySelector("details[open]");
            if (!currentlyOpen) {
                if (this.details.length > 0 && this.details[0].id) {
                    this.open(this.details[0].id);
                    [currentlyOpen] = this.details;
                }
            }
            if (currentlyOpen) {
                currentlyOpen.setAttribute("non-dismissible", "");
            }
            this.details.forEach((details => {
                details.addEventListener("on:accordion:opening", this.#boundHandleNonDismissible);
            }));
        }
        window.addEventListener("DOMContentLoaded", (() => {
            const outsideTriggers = document.querySelectorAll(Array.from(this.details).filter(el => el.id).map((el => `a[href="#${el.id}"]`)).toString());
            outsideTriggers.forEach((trigger => {
                trigger.addEventListener("click", (e => {
                    e.preventDefault();
                    this.open(trigger.getAttribute("href").slice(1));
                    const elementToScrollTo = document.querySelector(trigger.getAttribute("href"));
                    if (elementToScrollTo) {
                        let scrollOffset = 24;
                        const header = document.querySelector(".js-header");
                        if (header && header.classList.contains("js-header-sticky")) {
                            scrollOffset += header.offsetHeight;
                        }
                        const announcement = document.querySelector(".js-section__announcement");
                        if (announcement) {
                            scrollOffset += announcement.offsetHeight;
                        }
                        window.scrollTo({
                            top: elementToScrollTo.offsetTop + scrollOffset,
                            behavior: "smooth"
                        });
                    }
                }));
            }));
        }));
    }
    disconnectedCallback() {
        if (this.#singleOpen || this.#alwaysOpen) {
            this.details.forEach((details => {
                if (this.#singleOpen) details.removeEventListener("on:accordion:opening", this.#boundHandleSingleOpen);
                if (this.#alwaysOpen) details.removeEventListener("on:accordion:opening", this.#boundHandleNonDismissible);
            }));
        }
    }
    open(id) {
        if (id && this.accordions[id]) {
            this.accordions[id].open();
        }
    }
    close(id) {
        if (id && this.accordions[id]) {
            this.accordions[id].shrink();
        }
    }
    #handleSingleOpen(e) {
        const target = e.target.closest("details, .faq__accordion-item, .product-single__accordion-item");
        if (!target) return;
        const id = target.id;
        const currentlyOpen = Array.from(this.querySelectorAll("details[open], .faq__accordion-item:has(button[aria-expanded='true']), .product-single__accordion-item:has(button[aria-expanded='true'])"));
        Array.from(currentlyOpen).filter((el => el.id && el.id !== id)).forEach((el => {
            this.close(el.id);
        }));
    }
    #handleNonDismissible(e) {
        Array.from(this.querySelectorAll("details[non-dismissible]")).forEach((el => {
            el.removeAttribute("non-dismissible");
        }));
        e.target.setAttribute("non-dismissible", "");
    }
}

customElements.define("accordion-group", AccordionGroup);
//# sourceMappingURL=accordion-group.js.map
